import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Play, ChevronDown, ChevronUp, CheckCircle2, AlertCircle, Clock, RefreshCw } from "lucide-react";

const StatusDot = ({ ok }) => (
  <span className={`inline-flex items-center gap-1.5 text-xs font-mono ${ok === true ? "text-emerald-400" : ok === false ? "text-red-400" : "text-slate-500"}`}>
    {ok === true ? <CheckCircle2 className="w-3.5 h-3.5" /> : ok === false ? <AlertCircle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
    {ok === true ? "OK" : ok === false ? "FAIL" : "—"}
  </span>
);

const fmt = (iso) => {
  if (!iso) return "Never";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const metaSummary = (m) => {
  if (!m || typeof m !== "object") return "";
  const parts = [];
  if (m.skipped) parts.push(`skipped:${m.skipped}`);
  if (typeof m.bookings === "number") parts.push(`${m.bookings} bookings`);
  if (typeof m.recipients === "number") parts.push(`${m.recipients} recipients`);
  if (typeof m.corps === "number") parts.push(`${m.corps} corps`);
  if (typeof m.errors === "number" && m.errors > 0) parts.push(`${m.errors} err`);
  if (m.error) parts.push(`error`);
  return parts.join(" · ");
};

export default function CronHistoryPanel() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [running, setRunning] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/cron-history?limit=30");
      setData(data);
    } catch { toast.error("Failed to load system jobs"); }
  };
  useEffect(() => { load(); }, []);

  const runNow = async (name) => {
    setRunning(name);
    try {
      await api.post(`/admin/cron/${name}/run`);
      toast.success(`${name}: dispatched`);
      // Give the async task a beat, then reload
      setTimeout(load, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Trigger failed"); }
    finally { setRunning(null); }
  };

  if (!data) return <div className="text-slate-500 text-sm">Loading system jobs…</div>;

  return (
    <div className="space-y-5" data-testid="cron-history-panel">
      {/* Per-cron summary cards */}
      <div className="grid gap-3">
        {data.crons.map((c) => (
          <div
            key={c.name}
            className="bg-black/20 border border-white/5 rounded-lg p-4"
            data-testid={`cron-card-${c.name}`}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-amber-300 text-sm" data-testid={`cron-name-${c.name}`}>{c.name}</span>
                  <StatusDot ok={c.last_ok} />
                  <Badge className="bg-slate-700/40 border border-slate-600/40 text-slate-300 font-mono text-[10px]">{c.schedule}</Badge>
                </div>
                <div className="text-xs text-slate-400 mt-1">{c.description}</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3 text-xs">
                  <div><span className="text-slate-500 uppercase tracking-widest text-[10px] font-mono">Last run</span><div className="text-slate-300 font-mono" data-testid={`cron-last-${c.name}`}>{fmt(c.last_run_at)}</div></div>
                  <div><span className="text-slate-500 uppercase tracking-widest text-[10px] font-mono">Last success</span><div className="text-emerald-300 font-mono">{fmt(c.last_success_at)}</div></div>
                  <div><span className="text-slate-500 uppercase tracking-widest text-[10px] font-mono">Total runs</span><div className="text-slate-300 font-mono">{c.total_runs}</div></div>
                </div>
                {c.last_meta && Object.keys(c.last_meta).length > 0 && (
                  <div className="mt-2 text-[11px] text-slate-500 font-mono">{metaSummary(c.last_meta)}</div>
                )}
                {c.last_error && (
                  <div className="mt-2 text-[11px] text-red-300 bg-red-500/5 border border-red-500/20 rounded px-2 py-1 font-mono">
                    Last error ({fmt(c.last_error_at)}): {c.last_error}
                  </div>
                )}
              </div>
              <Button
                onClick={() => runNow(c.name)}
                disabled={running === c.name}
                size="sm"
                className="btn-gold"
                data-testid={`cron-run-${c.name}`}
              >
                <Play className="w-3.5 h-3.5 mr-1.5" /> {running === c.name ? "Running…" : "Run now"}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Full history table */}
      <div className="bg-black/20 border border-white/5 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-3 border-b border-white/5">
          <div className="text-sm font-semibold text-white">Recent runs</div>
          <Button onClick={load} size="sm" variant="outline" className="bg-transparent border-white/10 text-slate-300 h-8" data-testid="cron-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
        <div className="max-h-[520px] overflow-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-black/60 backdrop-blur">
              <tr className="text-slate-500 uppercase tracking-widest text-[10px] font-mono">
                <th className="text-left px-3 py-2 font-normal">When</th>
                <th className="text-left px-3 py-2 font-normal">Cron</th>
                <th className="text-left px-3 py-2 font-normal">Status</th>
                <th className="text-left px-3 py-2 font-normal">Summary</th>
                <th className="text-right px-3 py-2 font-normal">Details</th>
              </tr>
            </thead>
            <tbody>
              {data.history.length === 0 && (
                <tr><td colSpan="5" className="text-center text-slate-500 py-8" data-testid="cron-history-empty">No runs recorded yet. Trigger a job or wait for the next schedule.</td></tr>
              )}
              {data.history.map((r) => {
                const key = r.id || `${r.name}-${r.at}`;
                const isOpen = expanded === key;
                return (
                  <>
                    <tr key={key} className="border-t border-white/5" data-testid={`cron-row-${r.name}`}>
                      <td className="px-3 py-2 text-slate-300 font-mono whitespace-nowrap">{fmt(r.at)}</td>
                      <td className="px-3 py-2 text-amber-300 font-mono">{r.name}</td>
                      <td className="px-3 py-2"><StatusDot ok={r.ok} /></td>
                      <td className="px-3 py-2 text-slate-400 font-mono">{metaSummary(r.meta)}</td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => setExpanded(isOpen ? null : key)}
                          className="text-amber-400 hover:text-amber-300 inline-flex items-center gap-1"
                          data-testid={`cron-expand-${key}`}
                        >
                          {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />} {isOpen ? "Hide" : "View"}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr key={`${key}-body`} className="bg-black/40">
                        <td colSpan="5" className="px-4 py-3">
                          <pre className="text-[11px] text-slate-300 bg-black/40 border border-white/5 rounded p-3 overflow-auto whitespace-pre-wrap">
{JSON.stringify(r.meta || {}, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
